/**
 * Onboarding orchestrator for first-run enrollment.
 *
 * Drives the 14-screen flow: seven intro reveals, name input, a brief,
 * four voice questions, and a closing line. Each text screen types
 * itself out one character at a time with the emphasized words bolded
 * or underlined. A gently animating chevron at the bottom hints at
 * "swipe or tap" — the whole hit area advances the step.
 *
 * Layout is absolute-positioned so the text sits at the true visual
 * center of the viewport rather than wherever a flex-between wrapper
 * happens to land it. Safe-area insets are applied to the chevron
 * only so the text does not drift based on device shape.
 */
import { useState, type ReactNode } from "react";

import { usePlatform } from "@/hooks/usePlatform";

import { BRIEF_SCREEN, COMPLETE_SCREEN, INTRO_SCREENS, NAME_SCREEN, QUESTIONS } from "./content";
import { useOnboarding } from "./hooks";
import { QuestionScreen } from "./QuestionScreen";
import { SwipeHint } from "./SwipeHint";
import { TypedText } from "./TypedText";
import { useSwipe } from "./useSwipe";
import type { UserProfile } from "./types";

/**
 * Shared screen layout. Content sits dead-center of the viewport,
 * the advance hint floats at the bottom, and horizontal swipes
 * anywhere on the screen call through to onAdvance when advanceReady
 * is true.
 */
function Screen({
  content,
  advanceReady,
  advanceLabel,
  onAdvance,
}: {
  content: ReactNode;
  advanceReady: boolean;
  advanceLabel?: string;
  onAdvance: () => void;
}) {
  const swipe = useSwipe(() => {
    if (advanceReady) onAdvance();
  });
  return (
    <div className="absolute inset-0" {...swipe}>
      <div className="absolute inset-0 flex items-center justify-center px-8">
        {content}
      </div>
      <div className="absolute bottom-0 left-0 right-0 flex justify-center pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)]">
        <SwipeHint ready={advanceReady} onAdvance={onAdvance} label={advanceLabel} />
      </div>
    </div>
  );
}

function RevealScreen({ text, onNext }: { text: string; onNext: () => void }) {
  const { haptic } = usePlatform();
  const [ready, setReady] = useState(false);
  const advance = () => {
    haptic("light");
    onNext();
  };
  return (
    <Screen
      advanceReady={ready}
      onAdvance={advance}
      content={
        <p className="text-balance text-center text-[26px] font-medium leading-[1.35]">
          <TypedText text={text} onComplete={() => setReady(true)} />
        </p>
      }
    />
  );
}

function NameScreen({
  name,
  onChange,
  onNext,
}: {
  name: string;
  onChange: (v: string) => void;
  onNext: () => void;
}) {
  const { haptic } = usePlatform();
  const [introDone, setIntroDone] = useState(false);
  const [promptDone, setPromptDone] = useState(false);
  const canAdvance = name.trim().length > 0 && promptDone;

  const advance = () => {
    if (!canAdvance) return;
    haptic("medium");
    onNext();
  };

  return (
    <Screen
      advanceReady={canAdvance}
      onAdvance={advance}
      content={
        <div className="flex w-full max-w-sm flex-col gap-10">
          <div className="flex flex-col items-center gap-2">
            <p className="text-center text-3xl font-semibold">
              <TypedText text={NAME_SCREEN.headline} onComplete={() => setIntroDone(true)} />
            </p>
            <p className="min-h-[1.75rem] text-center text-xl text-muted-foreground">
              {introDone && (
                <TypedText
                  text={NAME_SCREEN.prompt}
                  onComplete={() => setPromptDone(true)}
                />
              )}
            </p>
          </div>

          <input
            autoFocus
            type="text"
            value={name}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") advance();
            }}
            placeholder={NAME_SCREEN.placeholder}
            disabled={!promptDone}
            className={`w-full border-0 border-b border-border bg-transparent pb-3 text-center text-2xl font-medium outline-none transition-opacity duration-700 focus:border-foreground ${
              promptDone ? "opacity-100" : "opacity-0"
            }`}
            autoCapitalize="words"
            autoComplete="given-name"
            spellCheck={false}
          />
        </div>
      }
    />
  );
}

function BriefScreen({ onNext }: { onNext: () => void }) {
  const { haptic } = usePlatform();
  const [lineIndex, setLineIndex] = useState(0);
  const [ready, setReady] = useState(false);

  const handleLineDone = () => {
    setLineIndex((i) => {
      if (i < BRIEF_SCREEN.lines.length - 1) return i + 1;
      setReady(true);
      return i;
    });
  };

  const advance = () => {
    haptic("medium");
    onNext();
  };

  return (
    <Screen
      advanceReady={ready}
      onAdvance={advance}
      advanceLabel="begin"
      content={
        <div className="flex flex-col items-center gap-4">
          {BRIEF_SCREEN.lines.map((line, i) => {
            if (i > lineIndex) return null;
            const isCurrent = i === lineIndex && !ready;
            return (
              <p key={line} className="text-center text-2xl font-medium">
                {isCurrent ? (
                  <TypedText text={line} onComplete={handleLineDone} />
                ) : (
                  line
                )}
              </p>
            );
          })}
        </div>
      }
    />
  );
}

function CompleteScreen() {
  return (
    <div className="absolute inset-0 flex items-center justify-center px-8">
      <p className="text-balance text-center text-3xl font-medium">
        <TypedText text={COMPLETE_SCREEN.line} />
      </p>
    </div>
  );
}

/**
 * Shows while the speech recognizer runs across the four answers.
 * No buttons, no advance affordance — the state machine flips to
 * complete automatically when every answer has been processed.
 * Three pulsing dots give the screen a heartbeat so it doesn't
 * feel frozen.
 */
function ProcessingScreen({ error }: { error: string | null }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-8 px-8">
      <p className="text-balance text-center text-2xl font-medium">
        <TypedText text="Listening back to what you said." />
      </p>
      <div className="flex items-center gap-2" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="h-2 w-2 rounded-full bg-foreground/50"
            style={{
              animation: "caret-blink 1.4s ease-in-out infinite",
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </div>
      {error && (
        <p className="max-w-xs text-balance text-center text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

interface OnboardingProps {
  onComplete: (profile: UserProfile) => void;
}

export function Onboarding({ onComplete }: OnboardingProps) {
  const { step, name, setName, advance, submitAnswer, processingError } = useOnboarding({
    onComplete,
  });

  let content: ReactNode;
  if (step.kind === "intro") {
    content = (
      <RevealScreen
        key={`intro-${step.index}`}
        text={INTRO_SCREENS[step.index]}
        onNext={advance}
      />
    );
  } else if (step.kind === "name") {
    content = <NameScreen name={name} onChange={setName} onNext={advance} />;
  } else if (step.kind === "brief") {
    content = <BriefScreen onNext={advance} />;
  } else if (step.kind === "question") {
    content = (
      <QuestionScreen
        key={`q-${step.index}`}
        question={QUESTIONS[step.index]}
        questionIndex={step.index}
        totalQuestions={QUESTIONS.length}
        onSubmit={submitAnswer}
      />
    );
  } else if (step.kind === "processing") {
    content = <ProcessingScreen error={processingError} />;
  } else {
    content = <CompleteScreen />;
  }

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden bg-background text-foreground">
      {content}
    </div>
  );
}
